import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AgentsService } from '../../../services/agents.service';
import { BillingPolicy, PLUS_INTELLIGENCE, UsageDailyRow } from '../../../models/agents.model';
import {
  billingModeHint,
  billingModeLabel,
  billingModePillClass,
  errorMessage,
  formatUsd,
  monthToDate,
  sumUsage,
  UsageTotals
} from '../../agents/agents-format';

/**
 * Businesses → profile → "Plus Intelligence" (specs/agents tasks-v1.2 G4). Read-only: the
 * business's model billing mode and what this month's cases have used. The mode and the
 * allowance come from the AI API's admin billing-policy route (CONTRACT-v1.2 §2; plan D25 —
 * managed is the only mode with runtime behaviour in v1.2, the others are recorded for the
 * deferred Studio); the month's executions come from the existing admin usage route. Both are
 * facts beside the subscription rather than things support changes here, so an AI API that is
 * dark (503) or unreachable shows one quiet line instead of an error banner.
 *
 * "This month" is the figure the fair-use gate enforces: the plus-intelligence agent's spend in
 * the UTC month (the billing-policy route and `FairUseGate` both read `usage/month` for that one
 * agent key). The website builder's executions never count against the allowance, so the row's
 * fallback sums only the intelligence rows and the usage window is the UTC month.
 */
@Component({
  selector: 'app-business-intelligence-panel',
  standalone: false,
  templateUrl: './intelligence-panel.component.html',
  styleUrls: ['./intelligence-panel.component.css']
})
export class BusinessIntelligencePanelComponent implements OnChanges {
  @Input() businessId?: string;

  policy: BillingPolicy | null = null;
  policyError = '';
  /** This month's `UsageDaily` rows for the business, every agent. */
  usage: UsageDailyRow[] = [];
  usageError = '';
  /** The usage call answered (possibly with no rows); only then do the rows stand in for a missing policy figure. */
  usageLoaded = false;
  loading = false;
  /** Set when the AI API answers 503 (agents switched off there) or not at all; replaces the panel body. */
  unavailable = '';

  readonly usd = formatUsd;
  readonly modeLabel = billingModeLabel;
  readonly modeHint = billingModeHint;
  readonly modePill = billingModePillClass;

  constructor(private agents: AgentsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) this.load();
  }

  load(): void {
    this.policy = null;
    this.usage = [];
    this.usageLoaded = false;
    this.policyError = '';
    this.usageError = '';
    this.unavailable = '';
    const id = this.businessId;
    if (!id) return;

    this.loading = true;
    let pending = 2;
    const done = () => {
      if (--pending === 0) this.loading = false;
    };
    this.agents.getBillingPolicy(id).subscribe({
      next: p => {
        this.policy = p;
        done();
      },
      error: err => {
        this.fail(err, 'policy');
        done();
      }
    });
    const { from, to } = monthToDate();
    this.agents.getUsage(from, to, id).subscribe({
      next: rows => {
        this.usage = rows ?? [];
        this.usageLoaded = true;
        done();
      },
      error: err => {
        this.fail(err, 'usage');
        done();
      }
    });
  }

  private fail(err: any, what: 'policy' | 'usage'): void {
    // Agents dark (503) or no answer at all (0): one line for the whole panel, nothing to alarm.
    if (err?.status === 503) {
      this.unavailable = 'Agents are switched off on the AI API.';
      return;
    }
    if (err?.status === 0) {
      this.unavailable = 'The AI API did not answer (network or CORS).';
      return;
    }
    const message = errorMessage(err, what === 'policy' ? 'Could not load the billing policy.' : "Could not load this month's usage.");
    if (what === 'policy') this.policyError = message;
    else this.usageError = message;
  }

  /** This month's Plus Intelligence cases (executions of `plus-intelligence`). */
  get cases(): UsageTotals {
    return sumUsage(this.usage.filter(r => r.agentKey === PLUS_INTELLIGENCE));
  }

  /**
   * Every agent's executions this month, for the "All agents" row only. The website builder does not
   * count against the Plus Intelligence allowance: the gate and the policy route measure the
   * intelligence agent alone, so nothing else in the panel is derived from this total.
   */
  get allAgents(): UsageTotals {
    return sumUsage(this.usage);
  }

  /** The included monthly allowance; null when the policy sets none (nothing to measure against). */
  get includedUsd(): number | null {
    const included = this.policy?.monthlyIncludedUsd;
    return typeof included === 'number' && included > 0 ? included : null;
  }

  get hardCapUsd(): number | null {
    const cap = this.policy?.hardCapUsd;
    return typeof cap === 'number' && cap > 0 ? cap : null;
  }

  /**
   * What the month has used, as the gate counts it: the policy route's own figure (the intelligence agent's
   * UTC-month spend), else the same measure summed from the intelligence rows when the policy call failed —
   * never every agent's cost, which would show a spend the gate does not enforce. Null until either answers.
   */
  get monthUsedUsd(): number | null {
    const fromPolicy = this.policy?.monthUsedUsd;
    if (typeof fromPolicy === 'number') return fromPolicy;
    return this.usageLoaded ? this.cases.cost : null;
  }

  /** "$0.420 used of $5.000 included" / "$0.420 used" / "—". */
  get monthLine(): string {
    const used = this.monthUsedUsd;
    if (used === null) return '—';
    const included = this.includedUsd;
    return included ? `${formatUsd(used)} used of ${formatUsd(included)} included` : `${formatUsd(used)} used`;
  }

  /** 0–100 share of the included allowance used; null when the policy sets no allowance. */
  get usedPct(): number | null {
    const included = this.includedUsd;
    const used = this.monthUsedUsd;
    if (included === null || used === null) return null;
    return Math.min(100, Math.round((used / included) * 100));
  }

  /** Past the included amount: new cases are refused with a plain message (plan D25), so support should see it. */
  get overIncluded(): boolean {
    const included = this.includedUsd;
    const used = this.monthUsedUsd;
    return included !== null && used !== null && used > included;
  }
}
